import React from "react";
import { useParams, Link } from "react-router-dom";
import industries from "../../../Data/industries";
import { FiChevronDown,FiChevronRight,FiHome ,FiCheck, FiInfo} from "react-icons/fi";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaCogs,
  FaGlobe,
  FaChartLine,
  FaQuoteLeft,
  FaBalanceScale,
  FaSearchLocation,
  FaBullseye,
  FaPenFancy,
  FaUserShield,
  FaGavel,
  FaExternalLinkAlt,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaUsers,
  FaChartBar
} from "react-icons/fa";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";

// Dynamic theme based on industry brand colors
const getTheme = (industry) => ({
  bg: "#0f172a",            // Dark blue-gray background
  surface: "#1e293b",       // Card surfaces
  surfaceElevated: "#334155", // Elevated surfaces
  primary: industry.brand.color || "#7c3aed",
  primaryLight: industry.brand.accent || "#8b5cf6",
  accent: "#f59e0b",        // Accent color
  textPrimary: "#f8fafc",   // Primary text
  textSecondary: "#94a3b8", // Secondary text
  border: "#334155",        // Border color
  success: "#10b981",       // Success color
  warning: "#f59e0b",       // Warning color
  info: "#3b82f6",          // Info color
  gradient: industry.brand.gradient || "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)",
  cardShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)",
  glowEffect: `0 0 15px ${industry.brand.color || 'rgba(124, 58, 237, 0.5)'}`
});

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      when: "beforeChildren"
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.8 } }
};

const scaleUp = {
  hidden: { scale: 0.95, opacity: 0 },
  visible: { scale: 1, opacity: 1, transition: { duration: 0.5 } }
};
export const handleScroll = () => {
  window.scrollTo({top:0 , behavior: "smooth"});
};

const IndustryDetailDark = () => {
  const { slug } = useParams();
  const industry = industries.find((ind) => ind.slug === slug);
  const theme = getTheme(industry);

  if (!industry) {
    return (
      <div className="max-w-5xl mx-auto py-16 text-center" style={{ backgroundColor: theme.bg }}>
        <h2 className="text-2xl font-bold mb-4 text-white">Industry not found</h2>
        <Link
          to="/industries"
          className="inline-flex items-center text-indigo-400 hover:text-indigo-300 transition-colors duration-300"
        >
          <FaArrowLeft className="mr-2" /> Back to Industries
        </Link>
      </div>
    );
  }

  // Get icon component
  const getIconComponent = (iconName) => {
    const icons = {
      FaBalanceScale: FaBalanceScale,
      FaSearchLocation: FaSearchLocation,
      FaBullseye: FaBullseye,
      FaPenFancy: FaPenFancy,
      FaUserShield: FaUserShield,
      FaGavel: FaGavel,
      FaCalendarAlt: FaCalendarAlt,
      FaMoneyBillWave: FaMoneyBillWave,
      FaUsers: FaUsers,
      FaChartBar: FaChartBar
    };
    return icons[iconName] || FaCheckCircle;
  };

  // Enhanced KPI icons
  const getKpiIcon = (index) => {
    const icons = [FaChartLine, FaUsers, FaMoneyBillWave, FaCalendarAlt, FaChartBar];
    return icons[index % icons.length];
  };

  const BreadCrumb = () => {
   return (
         <nav className="mb-8" aria-label="Breadcrumb">
           <ol className="hidden items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
             <li className="flex items-center">
               <Link to="/" onClick={handleScroll} className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors flex items-center">
                 <FiHome className="w-4 h-4 mr-1" />
                 Home
               </Link>
             </li>
             <li className="flex items-center">
               <FiChevronRight className="w-4 h-4 mx-1" />
               <span className="text-blue-600 dark:text-blue-400 font-medium" aria-current="page">Industries</span>
             </li>
           </ol>
           
           {/* Structured data for Google (invisible to users but helps SEO) */}
           <script type="application/ld+json">
             {JSON.stringify({
               "@context": "https://schema.org",
               "@type": "BreadcrumbList",
               "itemListElement": [{
                 "@type": "ListItem",
                 "position": 1,
                 "name": "Home",
                 "item": window.location.origin
               }, {
                 "@type": "ListItem",
                 "position": 2,
                 "name": "Industry",
                 "item": window.location.href
               },
               {
                  "@type": "ListItem",
                  "position": 3,
                  "name": industry.title,
                  "item": window.location.href
               }
              ]
             })}
           </script>
         </nav>
       );
  };


  return (
    <motion.div 
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="min-h-screen"
      style={{ backgroundColor: theme.bg, color: theme.textPrimary }}
    >
      <Helmet>
        <title>{industry.seo.metaTitle}</title>
        <meta name="description" content={industry.seo.metaDescription} />
        <meta name="keywords" content={industry.seo.keywords.join(', ')} />
        <meta property="og:title" content={industry.seo.og.title} />
        <meta property="og:description" content={industry.seo.og.description} />
        <meta property="og:image" content={industry.seo.og.image} />
        <meta name="twitter:card" content={industry.seo.twitter.card} />
        <meta name="twitter:handle" content={industry.seo.twitter.handle} />
        <script type="application/ld+json">
          {JSON.stringify(industry.schema.organization)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: industry.faqs.map(faq => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.a
              }
            }))
          })}
        </script>
        <link rel="canonical" href={`https://merndigital.com/industries/${industry.slug}`} />
      </Helmet>

      <BreadCrumb />

      {/* Hero Section */}
      <motion.section 
        variants={fadeIn}
        className="relative overflow-hidden mb-16 py-20 px-6 rounded-2xl mx-6"
        style={{ 
          background: theme.gradient,
          boxShadow: theme.glowEffect
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-60"></div>
        <div className="max-w-7xl mx-auto relative z-10 text-center">
          <motion.div 
            variants={itemVariants}
            className="mb-4"
          >
            <span 
              className="inline-block bg-black bg-opacity-30 px-4 py-1.5 rounded-full text-sm font-medium tracking-wider"
              style={{ color: theme.textPrimary }}
            >
              {industry.hero.badge}
            </span>
          </motion.div>
          
          <motion.h1 
            variants={itemVariants}
            className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight"
            style={{ textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}
          >
            {industry.hero.headline}
          </motion.h1>
          
          <motion.p 
            variants={itemVariants}
            className="text-xl md:text-2xl opacity-90 max-w-4xl mx-auto mb-8"
          >
            {industry.hero.subheadline}
          </motion.p>
          
          {/* Hero Highlights */}
          <motion.div 
            variants={containerVariants}
            className="flex flex-wrap justify-center gap-4 mt-8 mb-10"
          >
            {industry.hero.highlights.map((highlight, i) => (
              <motion.div
                key={i}
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
                className="bg-black bg-opacity-30 p-4 rounded-xl backdrop-blur-sm border border-white border-opacity-10"
              >
                <p className="text-2xl font-bold" style={{ color: theme.primaryLight }}>
                  {highlight.value}
                </p>
                <p className="text-sm opacity-80">{highlight.label}</p>
              </motion.div>
            ))}
          </motion.div>
          
          {/* Hero Buttons */}
          <motion.div 
            variants={containerVariants}
            className="flex flex-col sm:flex-row justify-center gap-4"
          >
            <Link
              to={industry.hero.primaryButton.href}
              className="px-8 py-3.5 bg-white rounded-lg shadow-lg hover:bg-gray-100 hover:shadow-xl transition-all duration-300 font-semibold flex items-center justify-center"
              style={{ 
                minWidth: '220px',
                color: theme.primary 
              }}
            >
              {industry.hero.primaryButton.label}
              <FaExternalLinkAlt className="ml-2" />
            </Link>
            <Link
              to={industry.hero.secondaryButton.href}
              className="px-8 py-3.5 border-2 border-white text-white rounded-lg hover:bg-white hover:bg-opacity-10 transition-all duration-300 font-semibold flex items-center justify-center"
              style={{ minWidth: '220px' }}
            >
              {industry.hero.secondaryButton.label}
            </Link>
          </motion.div>
        </div>
      </motion.section>

      {/* Main Content Container */}
      <div className="max-w-7xl mx-auto px-6 pb-20">
        {/* Summary Section */}
        <motion.section 
          variants={containerVariants}
          className="mb-16"
        >
          <motion.div 
            variants={scaleUp}
            className="p-8 rounded-2xl relative overflow-hidden"
            style={{ 
              backgroundColor: theme.surface,
              boxShadow: theme.cardShadow
            }}
          >
            <div className="absolute inset-0 opacity-10 bg-gradient-to-r from-indigo-500/10 to-purple-500/10"></div>
            <div className="relative z-10">
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <span 
                  className="w-6 h-6 rounded-full mr-3 flex items-center justify-center"
                  style={{ backgroundColor: theme.primary }}
                >
                  <FiCheck className="w-3 h-3 text-white" />
                </span>
                About Our {industry.title} Services
              </h2>
              <p className="opacity-90 text-lg leading-relaxed">{industry.summary}</p>
            </div>
          </motion.div>
        </motion.section>

        {/* Pain Points & Solutions Section */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {/* Pain Points */}
          <motion.section variants={containerVariants}>
            <motion.h2 
              variants={itemVariants}
              className="text-3xl font-bold mb-8 relative pb-2"
              style={{
                color: theme.textPrimary
              }}
            >
              <span 
                className="absolute bottom-0 left-0 w-16 h-1"
                style={{ background: theme.accent }}
              ></span>
              Industry Challenges
            </motion.h2>
            <div className="space-y-4">
              {industry.painPoints.map((point, i) => (
                <motion.div
                  key={i}
                  variants={itemVariants}
                  whileHover={{ y: -5, boxShadow: theme.glowEffect }}
                  className="p-6 rounded-xl flex items-start transition-all duration-300"
                  style={{ 
                    backgroundColor: theme.surfaceElevated,
                    borderLeft: `4px solid ${theme.primary}`,
                    boxShadow: theme.cardShadow
                  }}
                >
                  <div 
                    className="p-2 rounded-lg mr-4 flex-shrink-0"
                    style={{ backgroundColor: `${theme.primary}20` }}
                  >
                    <FiInfo className="w-5 h-5" />
                  </div>
                  <p className="opacity-90">{point}</p>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* Solutions */}
          <motion.section variants={containerVariants}>
            <motion.h2 
              variants={itemVariants}
              className="text-3xl font-bold mb-8 relative pb-2"
              style={{
                color: theme.textPrimary
              }}
            >
              <span 
                className="absolute bottom-0 left-0 w-16 h-1"
                style={{ background: theme.accent }}
              ></span>
              Our Solutions
            </motion.h2>
            <div className="space-y-6">
              {Object.entries(industry.solutions).map(([category, items]) => (
                <motion.div 
                  key={category} 
                  variants={itemVariants}
                  className="mb-8"
                >
                  <h3 className="text-xl font-semibold mb-4 capitalize flex items-center">
                    <span 
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white mr-3 text-sm flex-shrink-0"
                      style={{ 
                        backgroundColor: theme.primary,
                        boxShadow: `0 0 10px ${theme.primary}80`
                      }}
                    >
                      {category[0].toUpperCase()}
                    </span>
                    {category.replace(/([A-Z])/g, ' $1')}
                  </h3>
                  <ul className="space-y-3">
                    {items.map((solution, i) => (
                      <motion.li
                        key={i}
                        variants={itemVariants}
                        whileHover={{ x: 5 }}
                        className="flex items-start p-3 rounded-lg transition-all duration-200"
                        style={{ backgroundColor: `${theme.primary}10` }}
                      >
                        <div 
                          className="p-1 rounded-full mr-3 mt-1 flex-shrink-0"
                          style={{ backgroundColor: `${theme.success}20` }}
                        >
                          <FaCheckCircle 
                            className="text-sm" 
                            style={{ color: theme.success }} 
                          />
                        </div>
                        <span className="opacity-90">{solution}</span>
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.section>
        </div>

        {/* Services Section */}
        <motion.section 
          variants={containerVariants}
          className="mb-16"
        >
          <motion.h2 
            variants={itemVariants}
            className="text-3xl font-bold mb-8 relative pb-2"
          >
            <span 
              className="absolute bottom-0 left-0 w-16 h-1"
              style={{ background: theme.accent }}
            ></span>
            Tailored Services
          </motion.h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industry.services.map((service, i) => {
              const IconComponent = getIconComponent(service.icon);
              return (
                <motion.div
                  key={i}
                  variants={itemVariants}
                  whileHover={{ 
                    y: -8,
                    boxShadow: theme.glowEffect
                  }}
                  className="p-6 rounded-2xl transition-all duration-300 group relative overflow-hidden"
                  style={{ 
                    backgroundColor: theme.surface,
                    boxShadow: theme.cardShadow,
                    border: `1px solid ${theme.border}`
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10">
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center mb-5 transition-all duration-300 group-hover:bg-indigo-500 group-hover:text-white"
                      style={{ 
                        backgroundColor: `${theme.primary}20`,
                        color: theme.primary
                      }}
                    >
                      <IconComponent className="text-2xl" />
                    </div>
                    <h3 className="font-bold text-xl mb-2">{service.title}</h3>
                    <p className="opacity-80 mb-4">{service.description}</p>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm opacity-70 flex items-center">
                        <FaCalendarAlt className="mr-2" /> {service.duration}
                      </span>
                      <span className="text-sm font-medium" style={{ color: theme.primary }}>
                        ₹{service.startingPriceINR.toLocaleString()}
                      </span>
                    </div>
                    <ul className="space-y-2 text-sm opacity-90">
                      {service.deliverables.map((item, j) => (
                        <li key={j} className="flex items-start">
                          <span 
                            className="w-1.5 h-1.5 rounded-full mt-2 mr-2 flex-shrink-0"
                            style={{ backgroundColor: theme.primary }}
                          ></span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.section>

        {/* Features Section */}
        <motion.section 
          variants={containerVariants}
          className="mb-16"
        >
          <motion.h2 
            variants={itemVariants}
            className="text-3xl font-bold mb-8 relative pb-2"
          >
            <span 
              className="absolute bottom-0 left-0 w-16 h-1"
              style={{ background: theme.accent }}
            ></span>
            Feature Highlights
          </motion.h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <motion.div 
              variants={itemVariants}
              whileHover={{ scale: 1.01 }}
              className="p-8 rounded-2xl relative overflow-hidden"
              style={{ 
                backgroundColor: theme.surface,
                boxShadow: theme.cardShadow
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-teal-500/5 opacity-30"></div>
              <div className="relative z-10">
                <h3 className="text-xl font-bold mb-6 flex items-center">
                  <div 
                    className="w-8 h-8 rounded-full mr-3 flex items-center justify-center"
                    style={{ backgroundColor: theme.success }}
                  >
                    <FaCheckCircle className="text-white text-sm" />
                  </div>
                  Core Features
                </h3>
                <ul className="space-y-4">
                  {industry.features.mustHave.map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <div 
                        className="p-1 rounded-full mr-3 mt-1 flex-shrink-0"
                        style={{ backgroundColor: `${theme.success}20` }}
                      >
                        <FaCheckCircle 
                          className="text-xs" 
                          style={{ color: theme.success }} 
                        />
                      </div>
                      <span className="opacity-90">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
            
            <motion.div 
              variants={itemVariants}
              whileHover={{ scale: 1.01 }}
              className="p-8 rounded-2xl relative overflow-hidden"
              style={{ 
                backgroundColor: theme.surface,
                boxShadow: theme.cardShadow
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-500/5 opacity-30"></div>
              <div className="relative z-10">
                <h3 className="text-xl font-bold mb-6 flex items-center">
                  <div 
                    className="w-8 h-8 rounded-full mr-3 flex items-center justify-center"
                    style={{ backgroundColor: theme.primary }}
                  >
                    <FiCheck className="w-4 h-4 text-white" />
                  </div>
                  Premium Add-ons
                </h3>
                <ul className="space-y-4">
                  {industry.features.niceToHave.map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <div 
                        className="p-1 rounded-full mr-3 mt-1 flex-shrink-0"
                        style={{ backgroundColor: `${theme.primary}20` }}
                      >
                        <FiCheck className="w-3 h-3" style={{ color: theme.primary }} />
                      </div>
                      <span className="opacity-90">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Integrations Section */}
        <motion.section 
          variants={containerVariants}
          className="mb-16"
        >
          <motion.h2 
            variants={itemVariants}
            className="text-3xl font-bold mb-8 relative pb-2"
          >
            <span 
              className="absolute bottom-0 left-0 w-16 h-1"
              style={{ background: theme.accent }}
            ></span>
            Seamless Integrations
          </motion.h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {industry.integrations.map((int, i) => (
              <motion.div
                key={i}
                variants={itemVariants}
                whileHover={{ 
                  y: -5,
                  boxShadow: theme.glowEffect
                }}
                className="p-6 rounded-xl transition-all duration-300 group"
                style={{ 
                  backgroundColor: theme.surface,
                  boxShadow: theme.cardShadow,
                  border: `1px solid ${theme.border}`
                }}
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-5 group-hover:bg-indigo-500 group-hover:text-white transition-colors duration-300"
                  style={{ 
                    backgroundColor: `${theme.primary}20`,
                    color: theme.primary
                  }}
                >
                  <FaCogs className="text-2xl" />
                </div>
                <h3 className="font-bold text-lg mb-2">{int.name}</h3>
                <p className="text-sm opacity-80">{int.category}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Tech Stack Section */}
        <motion.section 
          variants={containerVariants}
          className="mb-16"
        >
          <motion.h2 
            variants={itemVariants}
            className="text-3xl font-bold mb-8 relative pb-2"
          >
            <span 
              className="absolute bottom-0 left-0 w-16 h-1"
              style={{ background: theme.accent }}
            ></span>
            Technology Stack
          </motion.h2>
          <div 
            className="p-8 rounded-2xl"
            style={{ 
              backgroundColor: theme.surface,
              boxShadow: theme.cardShadow
            }}
          >
            {Object.entries(industry.techStack).map(([key, values]) => (
              <motion.div 
                key={key} 
                variants={itemVariants}
                className="mb-8 last:mb-0"
              >
                <h3 className="font-bold text-xl mb-4 capitalize flex items-center">
                  <span 
                    className="w-6 h-6 rounded-full mr-3 flex items-center justify-center text-white text-sm"
                    style={{ backgroundColor: theme.primary }}
                  >
                    {key[0].toUpperCase()}
                  </span>
                  {key.replace(/([A-Z])/g, ' $1')}
                </h3>
                <div className="flex flex-wrap gap-3">
                  {values.map((tech, i) => (
                    <motion.span
                      key={i}
                      whileHover={{ 
                        scale: 1.05,
                        backgroundColor: `${theme.primary}30`
                      }}
                      className="px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200"
                      style={{ 
                        backgroundColor: `${theme.primary}20`,
                        color: theme.primary
                      }}
                    >
                      {tech}
                    </motion.span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Enhanced Process Section */}
        <motion.section 
  variants={containerVariants}
  className="mb-16"
>
  <motion.h2 
    variants={itemVariants}
    className="text-3xl font-bold mb-8 relative pb-2"
  >
    <span 
      className="absolute bottom-0 left-0 w-16 h-1"
      style={{ background: theme.accent }}
    ></span>
    Our Proven Process
  </motion.h2>
  
  <div className="relative">
    {/* Timeline connector - hidden on mobile */}
    <div 
      className="hidden md:block absolute left-4 top-0 h-full w-[4px] md:left-1/2 md:-ml-[2px]"
      style={{ 
        background: theme.gradient,
        boxShadow: `0 0 10px ${theme.primary}80`
      }}
    ></div>
    
    <ol className="space-y-8 md:space-y-12">
      {industry.process.map((step, i) => (
        <motion.li
          key={i}
          variants={itemVariants}
          className="relative"
        >
          {/* Mobile Layout (unchanged) */}
          <div className="md:hidden">
            <div className="flex items-start mb-4">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold mr-4 flex-shrink-0"
                style={{ 
                  background: theme.gradient,
                  boxShadow: `0 0 10px ${theme.primary}80`
                }}
              >
                {step.step}
              </div>
              <div>
                <h3 className="font-semibold text-lg">{step.title}</h3>
                <div className="flex items-center text-sm opacity-70 mt-1">
                  <FaCalendarAlt className="mr-2" />
                  <span>{step.duration}</span>
                </div>
              </div>
            </div>

            <motion.div 
              whileHover={{ scale: 1.01 }}
              className="p-6 rounded-2xl transition-all duration-300"
              style={{ 
                backgroundColor: theme.surface,
                boxShadow: theme.cardShadow,
                borderLeft: `4px solid ${theme.primary}`
              }}
            >
              <ul className="space-y-3">
                {step.outputs.map((o, j) => (
                  <motion.li 
                    key={j} 
                    className="flex items-start"
                    whileHover={{ x: 3 }}
                  >
                    <motion.span
                      className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0"
                      style={{ backgroundColor: theme.primary }}
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.8, 1, 0.8]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    ></motion.span>
                    <span className="text-sm md:text-base">{o}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Desktop/Tablet Layout - Fixed Version */}
          <div className="hidden md:flex w-full">
            {/* Left Side (even steps) */}
            <div className={`w-1/2 ${i % 2 === 0 ? 'pr-4' : 'invisible'}`}>
              {i % 2 === 0 && (
                <div className="flex justify-end h-full">
                  <div className="max-w-md w-full text-right">
                    <h3 className="font-semibold text-lg mb-1">{step.title}</h3>
                    <div className="flex justify-end items-center text-sm opacity-70 mb-3">
                      <FaCalendarAlt className="mr-2" />
                      <span>{step.duration}</span>
                    </div>
                    <motion.div 
                      whileHover={{ scale: 1.02 }}
                      className="p-6 rounded-2xl transition-all duration-300"
                      style={{ 
                        backgroundColor: theme.surface,
                        boxShadow: theme.cardShadow,
                        borderRight: `4px solid ${theme.primary}`
                      }}
                    >
                      <ul className="space-y-2">
                        {step.outputs.map((o, j) => (
                          <motion.li 
                            key={j} 
                            className="flex justify-end items-center"
                            whileHover={{ x: -3 }}
                          >
                            <span className="text-right">{o}</span>
                            <motion.span
                              className="w-2 h-2 rounded-full ml-3 flex-shrink-0"
                              style={{ backgroundColor: theme.primary }}
                              animate={{
                                scale: [1, 1.2, 1],
                                opacity: [0.8, 1, 0.8]
                              }}
                              transition={{
                                duration: 2,
                                repeat: Infinity,
                                ease: "easeInOut"
                              }}
                            ></motion.span>
                          </motion.li>
                        ))}
                      </ul>
                    </motion.div>
                  </div>
                </div>
              )}
            </div>

            {/* Timeline Dot Center */}
            <div className="w-12 flex justify-center">
              <div 
                className="w-6 h-6 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 z-10"
                style={{ 
                  background: theme.gradient,
                  boxShadow: `0 0 10px ${theme.primary}80`
                }}
              >
                {step.step}
              </div>
            </div>

            {/* Right Side (odd steps) */}
            <div className={`w-1/2 ${i % 2 !== 0 ? 'pl-4' : 'invisible'}`}>
              {i % 2 !== 0 && (
                <div className="max-w-md w-full">
                  <h3 className="font-semibold text-lg mb-1">{step.title}</h3>
                  <div className="flex items-center text-sm opacity-70 mb-3">
                    <FaCalendarAlt className="mr-2" />
                    <span>{step.duration}</span>
                  </div>
                  <motion.div 
                    whileHover={{ scale: 1.02 }}
                    className="p-6 rounded-2xl transition-all duration-300"
                    style={{ 
                      backgroundColor: theme.surface,
                      boxShadow: theme.cardShadow,
                      borderLeft: `4px solid ${theme.primary}`
                    }}
                  >
                    <ul className="space-y-2">
                      {step.outputs.map((o, j) => (
                        <motion.li 
                          key={j} 
                          className="flex items-center"
                          whileHover={{ x: 3 }}
                        >
                          <motion.span
                            className="w-2 h-2 rounded-full mr-3 flex-shrink-0"
                            style={{ backgroundColor: theme.primary }}
                            animate={{
                              scale: [1, 1.2, 1],
                              opacity: [0.8, 1, 0.8]
                            }}
                            transition={{
                              duration: 2,
                              repeat: Infinity,
                              ease: "easeInOut"
                            }}
                          ></motion.span>
                          <span>{o}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </motion.div>
                </div>
              )}
            </div>
          </div>
        </motion.li>
      ))}
    </ol>
  </div>
</motion.section>

        {/* KPIs Section */}
        <motion.section 
          variants={containerVariants}
          className="mb-16"
        >
          <motion.h2 
            variants={itemVariants}
            className="text-3xl font-bold mb-8 relative pb-2"
          >
            <span 
              className="absolute bottom-0 left-0 w-16 h-1"
              style={{ background: theme.accent }}
            ></span>
            Key Performance Indicators
          </motion.h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {industry.kpis.map((k, i) => {
              const KpiIcon = getKpiIcon(i);
              return (
                <motion.div
                  key={i}
                  variants={itemVariants}
                  whileHover={{ 
                    y: -5,
                    boxShadow: theme.glowEffect
                  }}
                  className="p-6 rounded-xl transition-all duration-300 flex flex-col items-center text-center"
                  style={{ 
                    backgroundColor: theme.surface,
                    boxShadow: theme.cardShadow,
                    border: `1px solid ${theme.border}`
                  }}
                >
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                    style={{ 
                      backgroundColor: `${theme.primary}20`,
                      color: theme.primary
                    }}
                  >
                    <KpiIcon className="text-2xl" />
                  </div>
                  <h3 className="font-medium mb-2">KPI {i+1}</h3>
                  <p className="text-sm opacity-90">{k}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.section>

        {/* Pricing Section */}
        <motion.section 
          variants={containerVariants}
          className="mb-16"
        >
          <motion.h2 
            variants={itemVariants}
            className="text-3xl font-bold mb-8 relative pb-2"
          >
            <span 
              className="absolute bottom-0 left-0 w-16 h-1"
              style={{ background: theme.accent }}
            ></span>
            Pricing Plans
          </motion.h2>
          <div className="grid md:grid-cols-3 gap-8">
            {industry.pricing.map((plan, i) => (
              <motion.div
                key={i}
                variants={itemVariants}
                whileHover={{ 
                  y: i === 1 ? -10 : -5,
                  boxShadow: i === 1 ? theme.glowEffect : theme.cardShadow
                }}
                className={`p-8 rounded-2xl transition-all duration-300 border-2 relative overflow-hidden ${
                  i === 1 ? 'md:-translate-y-3' : ''
                }`}
                style={{
                  backgroundColor: theme.surface,
                  boxShadow: theme.cardShadow,
                  borderColor: i === 1 ? theme.primary : theme.border
                }}
              >
                {i === 1 && (
                  <div 
                    className="absolute top-0 right-0 text-white text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg"
                    style={{ backgroundColor: theme.primary }}
                  >
                    POPULAR
                  </div>
                )}
                <h3 className="text-xl font-bold mb-2">{plan.tier}</h3>
                <p className="opacity-80 mb-4 text-sm">{plan.audience}</p>
                <p className="text-3xl font-bold mb-4" style={{ color: theme.primary }}>
                  ₹{plan.priceINR.toLocaleString()}
                  <span className="text-sm font-normal opacity-70"> / {plan.timeline}</span>
                </p>
                <ul className="space-y-3 mb-6">
                  {plan.includes.map((f, j) => (
                    <li key={j} className="flex items-center opacity-90">
                      <FaCheckCircle 
                        className="mr-3 flex-shrink-0" 
                        style={{ 
                          color: i === 1 ? theme.primary : theme.success 
                        }} 
                      /> 
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button 
                  className={`w-full py-3 rounded-lg font-medium transition-all duration-300 ${
                    i === 1 ? 'hover:opacity-90' : 'hover:bg-opacity-20'
                  }`}
                  style={{
                    background: i === 1 ? theme.gradient : 'transparent',
                    color: i === 1 ? 'white' : theme.primary,
                    border: i !== 1 ? `1px solid ${theme.primary}` : 'none',
                    boxShadow: i === 1 ? `0 4px 15px ${theme.primary}40` : 'none'
                  }}
                >
                  Get Started
                </button>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Case Studies Section */}
        {industry.caseStudies && industry.caseStudies.length > 0 && (
          <motion.section 
            variants={containerVariants}
            className="mb-16"
          >
            <motion.h2 
              variants={itemVariants}
              className="text-3xl font-bold mb-8 relative pb-2"
            >
              <span 
                className="absolute bottom-0 left-0 w-16 h-1"
                style={{ background: theme.accent }}
              ></span>
              Success Stories
            </motion.h2>
            <div className="grid md:grid-cols-2 gap-8">
              {industry.caseStudies.map((caseStudy, i) => (
                <motion.div
                  key={i}
                  variants={itemVariants}
                  whileHover={{ 
                    scale: 1.01,
                    boxShadow: theme.glowEffect
                  }}
                  className="p-8 rounded-2xl transition-all duration-300 relative overflow-hidden"
                  style={{ 
                    backgroundColor: theme.surface,
                    boxShadow: theme.cardShadow
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-500/5 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10">
                    <h3 className="font-bold text-xl mb-2">{caseStudy.name}</h3>
                    <p className="opacity-80 mb-4 flex items-center">
                      <FaSearchLocation className="mr-2" /> {caseStudy.location}
                    </p>
                    
                    <div className="mb-6">
                      <h4 className="font-medium mb-2 flex items-center">
                        <FaBullseye className="mr-2" /> Goals:
                      </h4>
                      <ul className="list-disc list-inside space-y-1 pl-4 opacity-90">
                        {caseStudy.goals.map((goal, j) => (
                          <li key={j}>{goal}</li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="mb-6">
                      <h4 className="font-medium mb-2 flex items-center">
                        <FaChartLine className="mr-2" /> Outcomes:
                      </h4>
                      <ul className="list-disc list-inside space-y-1 pl-4 opacity-90">
                        {caseStudy.outcomes.map((outcome, j) => (
                          <li key={j}>{outcome}</li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-medium mb-2 flex items-center">
                        <FaCogs className="mr-2" /> Tech Stack:
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {caseStudy.stack.map((tech, j) => (
                          <span 
                            key={j} 
                            className="px-3 py-1 rounded-full text-sm"
                            style={{ 
                              backgroundColor: `${theme.primary}20`,
                              color: theme.primary
                            }}
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.section>
        )}

        {/* Testimonials Section */}
        <motion.section 
          variants={containerVariants}
          className="mb-16"
        >
          <motion.h2 
            variants={itemVariants}
            className="text-3xl font-bold mb-8 relative pb-2"
          >
            <span 
              className="absolute bottom-0 left-0 w-16 h-1"
              style={{ background: theme.accent }}
            ></span>
            Client Testimonials
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-8">
            {industry.testimonials.map((t, i) => (
              <motion.div
                key={i}
                variants={itemVariants}
                whileHover={{ 
                  scale: 1.01,
                  boxShadow: theme.glowEffect
                }}
                className="p-8 rounded-2xl transition-all duration-300 relative overflow-hidden"
                style={{ 
                  backgroundColor: theme.surface,
                  boxShadow: theme.cardShadow
                }}
              >
                <div 
                  className="absolute top-6 left-6 opacity-10 text-6xl"
                  style={{ color: theme.primary }}
                >
                  <FaQuoteLeft />
                </div>
                <div className="relative z-10">
                  <p className="text-lg mb-6 opacity-90">"{t.quote}"</p>
                  <div className="flex items-center">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold mr-4 flex-shrink-0"
                      style={{ 
                        background: theme.gradient,
                        boxShadow: `0 0 10px ${theme.primary}80`
                      }}
                    >
                      {t.author[0]}
                    </div>
                    <div>
                      <h4 className="font-semibold">{t.author}</h4>
                      <p className="text-sm opacity-70">{t.role}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* FAQs Section */}
        <motion.section 
          variants={containerVariants}
          className="mb-16"
        >
          <motion.h2 
            variants={itemVariants}
            className="text-3xl font-bold mb-8 relative pb-2"
          >
            <span 
              className="absolute bottom-0 left-0 w-16 h-1"
              style={{ background: theme.accent }}
            ></span>
            Frequently Asked Questions
          </motion.h2>
          <div 
            className="rounded-2xl overflow-hidden"
            style={{ 
              backgroundColor: theme.surface,
              boxShadow: theme.cardShadow
            }}
          >
            {industry.faqs.map((faq, i) => (
              <motion.div 
                key={i}
                variants={itemVariants}
                className="border-b last:border-b-0"
                style={{ borderColor: theme.border }}
              >
                <details className="group">
                  <summary className="flex justify-between items-center p-6 cursor-pointer transition-colors duration-200 hover:bg-opacity-10 hover:bg-white">
                    <h3 className="font-medium">{faq.q}</h3>
                    <FiChevronDown className="w-5 h-5 opacity-70 group-open:rotate-180 transition-transform duration-200" />
                  </summary>
                  <div className="px-6 pb-6 pt-0 opacity-90">
                    <p>{faq.a}</p>
                  </div>
                </details>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Localization Section */}
        {industry.localization && (
          <motion.section 
            variants={containerVariants}
            className="mb-16"
          >
            <motion.h2 
              variants={itemVariants}
              className="text-3xl font-bold mb-8 relative pb-2"
            >
              <span 
                className="absolute bottom-0 left-0 w-16 h-1"
                style={{ background: theme.accent }}
              ></span>
              Global Support
            </motion.h2>
            <motion.div 
              variants={scaleUp}
              className="p-8 rounded-2xl relative overflow-hidden"
              style={{ 
                backgroundColor: theme.surface,
                boxShadow: theme.cardShadow
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-500/5 opacity-30"></div>
              <div className="relative z-10">
                <div className="flex flex-wrap gap-3 mb-4">
                  {industry.localization.languages.map((loc, i) => (
                    <motion.span
                      key={i}
                      whileHover={{ scale: 1.05 }}
                      className="px-4 py-2 rounded-full text-sm font-medium flex items-center transition-colors duration-200"
                      style={{ 
                        backgroundColor: `${theme.primary}20`,
                        color: theme.primary
                      }}
                    >
                      <FaGlobe className="mr-2" />
                      {loc}
                    </motion.span>
                  ))}
                </div>
                <p className="text-sm opacity-80">
                  <span className="font-medium">Default language:</span> {industry.localization.default.toUpperCase()}
                </p>
              </div>
            </motion.div>
          </motion.section>
        )}

        {/* CTA Section */}
        <motion.section 
          variants={fadeIn}
          className="text-center py-16 rounded-2xl relative overflow-hidden"
          style={{ 
            background: theme.gradient,
            boxShadow: theme.glowEffect
          }}
        >
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-gradient-to-r from-black to-transparent"></div>
          </div>
          <div className="relative z-10 max-w-3xl mx-auto px-6">
            <motion.h2 
              variants={itemVariants}
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ textShadow: '0 2px 5px rgba(0,0,0,0.2)' }}
            >
              {industry.cta.title}
            </motion.h2>
            <motion.p 
              variants={itemVariants}
              className="text-xl opacity-90 mb-8"
            >
              {industry.cta.text}
            </motion.p>
            <motion.div 
              variants={containerVariants}
              className="flex flex-col sm:flex-row justify-center gap-4"
            >
              <Link
              onClick={handleScroll}
                to={industry.cta.primaryButton.href}
                className="px-8 py-4 bg-white rounded-xl shadow-lg hover:bg-gray-100 hover:shadow-xl transition-all duration-300 font-semibold text-lg flex items-center justify-center"
                style={{ 
                  minWidth: '220px',
                  color: theme.primary 
                }}
              >
                {industry.cta.primaryButton.label}
                <FaExternalLinkAlt className="ml-2" />
              </Link>
              <Link
                to={industry.cta.secondaryButton.href}
                className="px-8 py-4 border-2 border-white text-white rounded-xl hover:bg-white hover:bg-opacity-10 transition-all duration-300 font-semibold text-lg flex items-center justify-center"
                style={{ minWidth: '220px' }}
              >
                {industry.cta.secondaryButton.label}
              </Link>
            </motion.div>
          </div>
        </motion.section>
      </div>
    </motion.div>
  );
};

export default IndustryDetailDark;